import React, { useState } from "react";
import axios from "axios";
import { BASE_URL } from "config/config";
import { Modal, Button, Dropdown } from "react-bootstrap";
import "./DocumentCard.scss";
import { useTranslation } from "react-i18next";
export const DocumentCard = ({ doc }) => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [file, setFile] = useState(null);
  const [docDetails, setDocDetails] = useState(null);
  const [versions, setVersions] = useState([]);
  const { t } = useTranslation();

  // Helper functions for document information
  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || 'file';
  };

  const getFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return Math.round(sizeInBytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileTypeIcon = (extension) => {
    const iconMap = {
      'pdf': 'fas fa-file-pdf',
      'doc': 'fas fa-file-word',
      'docx': 'fas fa-file-word',
      'xls': 'fas fa-file-excel',
      'xlsx': 'fas fa-file-excel',
      'ppt': 'fas fa-file-powerpoint',
      'pptx': 'fas fa-file-powerpoint',
      'txt': 'fas fa-file-alt',
      'jpg': 'fas fa-file-image',
      'jpeg': 'fas fa-file-image',
      'png': 'fas fa-file-image',
      'gif': 'fas fa-file-image',
      'zip': 'fas fa-file-archive',
      'rar': 'fas fa-file-archive',
      'mp4': 'fas fa-file-video',
      'avi': 'fas fa-file-video',
      'mp3': 'fas fa-file-audio',
      'wav': 'fas fa-file-audio'
    };
    return iconMap[extension] || 'fas fa-file';
  };

  const getFileTypeColor = (extension) => {
    const colorMap = {
      'pdf': '#e74c3c',
      'doc': '#3498db',
      'docx': '#3498db',
      'xls': '#27ae60',
      'xlsx': '#27ae60',
      'ppt': '#f39c12',
      'pptx': '#f39c12',
      'txt': '#95a5a6',
      'jpg': '#e67e22',
      'jpeg': '#e67e22',
      'png': '#e67e22',
      'gif': '#e67e22',
      'zip': '#9b59b6',
      'rar': '#9b59b6',
      'mp4': '#e91e63',
      'avi': '#e91e63',
      'mp3': '#ff5722',
      'wav': '#ff5722'
    };
    return colorMap[extension] || '#6c757d';
  };
  function getImageURL(docId, versionId) {
    if (versionId) {
      return `${BASE_URL}documentsFile/${docId}/file?versionId=${versionId}`;
    }
    return `${BASE_URL}documentsFile/${docId}/file`;
  }
  const downloadVersion = (docId) => {
    const url = `${BASE_URL}documentsFile/${docId}/file/download`;
    const link = document.createElement("a");
    link.href = url;
    link.click(); // le serveur force le download, pas besoin de `download` ici
  };
  // ==========================
  // Upload document
  // ==========================
  const handleUpload = async () => {
    if (!file) return alert(t("ged.selectFile"));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user", "admin"); // remplacer par user connecté

    try {
      await axios.post(`${BASE_URL}documentsFile/${doc._id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Document mis à jour !");
      setShowUpdateModal(false);
    } catch (err) {
      console.error(err);
      alert(t("ged.errorUploadingDocument"));
    }
  };

  // ==========================
  // Consulter détails
  // ==========================
  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${BASE_URL}documentsFile/${doc._id}`);
      setDocDetails(res?.data?.data);
      setShowConsultModal(true);
    } catch (err) {
      console.error(err);
      alert(t("ged.impossibleToGetDetails"));
    }
  };

  // ==========================
  // Liste versions
  // ==========================
  const fetchVersions = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}documentsFile/${doc._id}/versions`
      );
      setVersions(res?.data?.data);
      setShowVersionsModal(true);
    } catch (err) {
      console.error(err);
      alert(t("ged.impossibleToGetVersions"));
    }
  };

  const fileExtension = getFileExtension(doc.title);
  const fileTypeIcon = getFileTypeIcon(fileExtension);
  const fileTypeColor = getFileTypeColor(fileExtension);

  return (
    <div className="document-card" data-type={fileExtension}>
      <div className="document-header">
        <div className="document-icon" style={{ color: fileTypeColor }}>
          <i className={fileTypeIcon}></i>
        </div>
        <div className="document-info">
          <h4 className="document-title" title={doc.title}>
            {doc.title}
          </h4>
          <div className="document-meta">
            <span className="file-type">{fileExtension.toUpperCase()}</span>
            <span className="file-size">{getFileSize(doc.size)}</span>
            <span className="file-date">{formatDate(doc.updatedAt || doc.createdAt)}</span>
          </div>
        </div>
        <div className="document-actions">
          <Dropdown flip>
            <Dropdown.Toggle className="action-toggle">
              <i className="fas fa-ellipsis-v"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="action-menu" flip>
              <Dropdown.Item onClick={() => setShowUpdateModal(true)}>
                <i className="fas fa-upload me-2"></i>
                {t("ged.updateVersion")}
              </Dropdown.Item>
              <Dropdown.Item onClick={fetchDetails}>
                <i className="fas fa-eye me-2"></i>
                {t("ged.consult")}
              </Dropdown.Item>
              <Dropdown.Item onClick={fetchVersions}>
                <i className="fas fa-history me-2"></i>
                {t("ged.versions")}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item 
                onClick={() => window.open(getImageURL(doc._id), '_blank')}
                className="text-primary"
              >
                <i className="fas fa-external-link-alt me-2"></i>
                Ouvrir
              </Dropdown.Item>
              <Dropdown.Item 
                onClick={() => downloadVersion(doc._id)}
                className="text-success"
              >
                <i className="fas fa-download me-2"></i>
                Télécharger
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      
      <div className="document-footer">
        <div className="document-stats">
          <span className="stat-item">
            <i className="fas fa-user me-1"></i>
            {doc.author || 'Inconnu'}
          </span>
          <span className="stat-item">
            <i className="fas fa-clock me-1"></i>
            v{doc.currentVersion || '1'}
          </span>
        </div>
        <div className="document-status">
          <span className="status-badge status-active">
            <i className="fas fa-check-circle me-1"></i>
            Actif
          </span>
        </div>
      </div>

      {/* Modal Update Version */}
      <Modal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        centered
        style={{ borderRadius: "15px" }}
        className="modal-create-folder"
        id={"modal-create-folder"}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ged.updateVers")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input type="file" onChange={(e) => setFile(e.target.files[0])} />
        </Modal.Body>
        <Modal.Footer>
          <Button className="w-100 btn-create" onClick={handleUpload}>
            {t("ged.uploderr")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Consulter */}
      <Modal
        show={showConsultModal}
        onHide={() => setShowConsultModal(false)}
        centered
        style={{ borderRadius: "15px" }}
        className="modal-create-folder"
        id={"modal-create-folder"}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ged.details")} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {docDetails ? (
            <>
              <p>
                <strong>{t("ged.title")}:</strong> {docDetails.title}
              </p>
              <p>
                <strong>ID:</strong> {docDetails._id}
              </p>
              <p>
                <strong>{t("ged.auteur")}:</strong> {docDetails.author}
              </p>
              <p>
                <strong>{t("ged.versionCourante")}:</strong>{" "}
                {docDetails.currentVersion}
              </p>
              <p>
                <strong>{t("ged.createdAt")}:</strong> {docDetails.createdAt}
              </p>
              <p>
                <strong>{t("ged.updatedAt")}:</strong> {docDetails.updatedAt}
              </p>
              <p>
                <strong>URL:</strong>{" "}
                <a
                  href={getImageURL(docDetails._id)}
                  target="_blank"
                  rel="noopener noreferrer">
                  {t("ged.voir")}
                </a>
              </p>
              <p>
                <strong>{t("ged.telecharger")}:</strong>{" "}
                <button
                  className="btn btn-link p-0"
                  onClick={() => downloadVersion(docDetails._id)}>
                  {t("ged.telecharger")}
                </button>
              </p>
            </>
          ) : (
            <p>Chargement...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConsultModal(false)}>
            {t("ged.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Versions */}
      <Modal
        show={showVersionsModal}
        onHide={() => setShowVersionsModal(false)}
        centered
        style={{ borderRadius: "15px" }}
        className="modal-create-folder"
        id={"modal-create-folder"}>
        <Modal.Header closeButton>
          <Modal.Title> {t("ged.versionss")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {versions.length > 0 ? (
            <ul>
              {versions.map((v) => (
                <li key={v._id}>
                  {" "}
                  <a
                    href={getImageURL(doc._id, v._id)}
                    target="_blank"
                    rel="noopener noreferrer">
                    {v.versionName || v._id}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p>{t("ged.aucun")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowVersionsModal(false)}>
            {t("ged.close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
